import { Component, Vue } from 'vue-facing-decorator';
import { GoldMedal, Search } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import Stats from '@/components/Stats/Stats.vue';
import AuthService from '@/services/AuthService';
import ApiService from '@/services/ApiService';

interface CalendarEntry
{
    day: string;
    class: string;
    title: string;
    note: string;
    isdefault : boolean;
}

@Component(
{
    components:
    {
        Header,
        Navigation,
        Stats,
        GoldMedal,
        Search
    },
    enums:
    {
        NavigationIndex
    }
})

export default class Home extends Vue 
{
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;

    private loading : boolean = true;
    private loadingMetrics : boolean = true;
    private loadingMetricsMissingReferences : boolean = false;
    private loadingMetricsMissingTargetgroups : boolean = false;
    private isCustomer : boolean = false;
    private hasPermission: boolean[] = [] as boolean[];
    private testModelList : TestModel[] = [] as TestModel[];
    private targetGroupModelList : TargetGroupModel[] = [] as TargetGroupModel[];
    private metricCountModel : MetricCounts = {} as MetricCounts;
    private metricTargetgroupList : MetricTargetGroup[] = [] as MetricTargetGroup[];
    private metricMissingReferenceList : MetricMissingReference[] = [] as MetricMissingReference[];
    private metricMissingReferenceDisplayList : MetricMissingReference[] = [] as MetricMissingReference[];
    private metricMissingReferenceFilteredList : MetricMissingReference[] = [] as MetricMissingReference[];
    private metricMissingTargetgroupList : MetricMissingTargetgroup[] = [] as MetricMissingTargetgroup[];
    private metricMissingTargetgroupDisplayList : MetricMissingTargetgroup[] = [] as MetricMissingTargetgroup[];
    private metricMissingTargetgroupFilteredList : MetricMissingTargetgroup[] = [] as MetricMissingTargetgroup[];
    private currentDate : Date = new Date();
    private showMissingReferences : boolean = false;
    private showMissingTargetgroups : boolean = false;
    private referencePage : number = 1;
    private referenceRowsPerPage : number = 10;
    private referencePagerCount : number = 19;
    private filterMissingReferences : string = '';
    private targetgroupPage : number = 1;
    private targetgroupRowsPerPage : number = 10;
    private targetgroupPagerCount : number = 19;
    private filterMissingTargetgroup : string = '';

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['DASH_CALENDAR'] = AuthService.hasPermission('DASH_CALENDAR');
        this.hasPermission['DASH_STATS'] = AuthService.hasPermission('DASH_STATS');
        this.hasPermission['DASH_TESTS'] = AuthService.hasPermission('DASH_TESTS');
        this.hasPermission['DASH_QUESTIONS'] = AuthService.hasPermission('DASH_QUESTIONS');
        this.hasPermission['TEST_SELECT'] = AuthService.hasPermission('TEST_SELECT');
        
        this.isCustomer = await AuthService.isCustomer();

        if (this.hasPermission['TEST_SELECT'])
        {
            await this.loadTests();
            await this.loadTargetGroups();
            await this.loadMetricCounts();
            await this.loadMetricTargetGroupCounts();
        }

        // set loading indicator
        window.addEventListener('resize', this.onWindowResize);
        window.scrollTo(0,0);

        this.loading = false;
    }
    
    public beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

    private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private async loadTests()
    {
        this.loading = true;
        this.testModelList = await ApiService.getAllTests();
        this.loading = false;
    }
    
    private async loadTargetGroups()
    {
        this.targetGroupModelList = await ApiService.getAllTargetGroups();
    }
    
    private async loadMetricCounts()
    {
        this.loadingMetrics = true;
        this.metricCountModel = await ApiService.getMetricCounts() as MetricCounts;
        this.loadingMetrics = false;
    }

    private async loadMetricMissingReferences()
    {
        this.loadingMetricsMissingReferences = true;
        this.metricMissingReferenceList = await ApiService.getMetricMissingReferences() as MetricMissingReference[];
        this.loadingMetricsMissingReferences = false;
    }

    private async loadMetricMissingTargetgroups()
    {
        this.loadingMetricsMissingTargetgroups = true;
        this.metricMissingTargetgroupList = await ApiService.getMetricMissingTargetgroups() as MetricMissingTargetgroup[];
        this.loadingMetricsMissingTargetgroups = false;
    }

    private async loadMetricTargetGroupCounts()
    {
        this.metricTargetgroupList = await ApiService.getMetricTargetGroupCounts() as MetricTargetGroup[];
    }

    public getCalendarEvents(data : any) : CalendarEntry[]
    {
        let entries : CalendarEntry[] = [] as CalendarEntry[];
        let day = data.day.split('-').slice(1)[1];

        
        if (this.testModelList && this.testModelList.length > 0)
        {
            for (let t : TestModel of this.testModelList)
            {
                let calDate : string = new Intl.DateTimeFormat('de-DE', {year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date(data.day));
                let testDate : string = new Intl.DateTimeFormat('de-DE', {year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date(0).setUTCSeconds(t.testdate));

                if (t.datedelete == null && calDate == testDate)
                {
                    let entry : CalendarEntry = {} as CalendarEntry;
                    entry.day = day;
                    entry.title = t.name;
                    entry.testid = t.testid;
                    entry.class = "blue";
                    entry.isdefault = false;
                    entries.push(entry);
                }
            }
        }

        return entries;
    }

    private async showMissingReferencePage()
    {
        // reset display list
        this.metricMissingReferenceDisplayList = [] as MetricMissingReference[];

        // only add current page records to the display list
        let i:number = 0;
        if (this.metricMissingReferenceList && this.metricMissingReferenceFilteredList)
        {
            for (i = 0; i < this.metricMissingReferenceFilteredList.length; i++)
            {
                if (i >= (this.referencePage-1)*this.referenceRowsPerPage && i < this.referencePage*this.referenceRowsPerPage)
                {
                    this.metricMissingReferenceDisplayList.push(this.metricMissingReferenceFilteredList![i]);
                }
            }
        }

        return this.referencePage;
    }

    private async filterMissingReferencesList()
    {   
        // reset filtered list
        this.metricMissingReferenceFilteredList = [] as MetricMissingReference[];
        
        // if a filter is set, add remaining records to filtered list
        if (this.filterMissingReferences.length > 0)
        {
            let i:number = 0;
            for (i = 0; i < this.metricMissingReferenceList.length; i++)
            {
                if (this.metricMissingReferenceList[i].question.toLowerCase().includes(this.filterMissingReferences.toLowerCase())
                   || this.metricMissingReferenceList[i].option.toLowerCase().includes(this.filterMissingReferences.toLowerCase())
                   || this.metricMissingReferenceList[i].question_position.toString().toLowerCase().includes(this.filterMissingReferences.toLowerCase())
                   || this.metricMissingReferenceList[i].option_position.toString().toLowerCase().includes(this.filterMissingReferences.toLowerCase()))
                {
                    this.metricMissingReferenceFilteredList.push(this.metricMissingReferenceList[i]);
                }
            }
            this.referencePage = 1;
        }
        else
        {
            this.metricMissingReferenceFilteredList = this.metricMissingReferenceList;
        }

        return this.filterMissingReferences;
    }

    private async showMissingTargetgroupPage()
    {
        // reset display list
        this.metricMissingTargetgroupDisplayList = [] as MetricMissingTargetgroup[];

        // only add current page records to the display list
        let i:number = 0;
        if (this.metricMissingTargetgroupList && this.metricMissingTargetgroupFilteredList)
        {
            for (i = 0; i < this.metricMissingTargetgroupFilteredList.length; i++)
            {
                if (i >= (this.targetgroupPage-1)*this.targetgroupRowsPerPage && i < this.targetgroupPage*this.targetgroupRowsPerPage)
                {
                    this.metricMissingTargetgroupDisplayList.push(this.metricMissingTargetgroupFilteredList![i]);
                }
            }
        }

        return this.targetgroupPage;
    }

    private async filterMissingTargetgroupList()
    {   
        // reset filtered list
        this.metricMissingTargetgroupFilteredList = [] as MetricMissingTargetgroup[];
        
        // if a filter is set, add remaining records to filtered list
        if (this.filterMissingTargetgroup.length > 0)
        {
            let i:number = 0;
            for (i = 0; i < this.metricMissingTargetgroupList.length; i++)
            {
                if (this.metricMissingTargetgroupList[i].question.toLowerCase().includes(this.filterMissingTargetgroup.toLowerCase())
                   || this.metricMissingTargetgroupList[i].position.toString().toLowerCase().includes(this.filterMissingTargetgroup.toLowerCase()))
                {
                    this.metricMissingTargetgroupFilteredList.push(this.metricMissingTargetgroupList[i]);
                }
            }
            this.targetgroupPage = 1;
        }
        else
        {
            this.metricMissingTargetgroupFilteredList = this.metricMissingTargetgroupList;
        }

        return this.filterMissingTargetgroups;
    }

    private onDateClick(testid : any)
    {
        this.$router.push(`/test/${testid}`);
    }

    private onReferencePageChange(p : number)
    {
        this.referencePage = p;
        this.showMissingReferencePage();
    }

    private onTargetgroupPageChange(p : number)
    {
        this.targetgroupPage = p;
        this.showMissingTargetgroupPage();
    }

    private getMetricValue(type: number, name : string) : number
    {
        if (!this.metricTargetgroupList) return 0;

        for (let tg of this.metricTargetgroupList)
        {
            if (tg.type == type && tg.name == name)
            {
                return tg.count;
            }
        }
    }

    private getMetricSum(type: number) : number
    {
        let sum : number = 0;

        if (!this.metricTargetgroupList) return 0;
        
        for (let tg of this.metricTargetgroupList)
        {
            if (tg.type == type)
            {
                sum += Number(tg.count);
            }
        }

        return sum;
    }

    private async onMissingReferencesClick()
    {
        this.showMissingReferences = true;
        await this.loadMetricMissingReferences();
        
        if (this.metricMissingReferenceList)
        {
            await this.filterMissingReferencesList();
            await this.showMissingReferencePage();
        }
    }

    private onFilterMissingReferencesChange()
    {
        this.filterMissingReferencesList();
        this.showMissingReferencePage();
    }

    private onMissingReferenceSelect(item : MetricMissingReference)
    {
        window.open(`${process.env.VUE_APP_BASE_URL}question/${item.questionid}/${item.optionid}`, '_blank')
    }

    private async onMissingTargetgroupClick()
    {
        this.showMissingTargetgroups = true;
        await this.loadMetricMissingTargetgroups();
        
        if (this.metricMissingTargetgroupList)
        {
            await this.filterMissingTargetgroupList();
            await this.showMissingTargetgroupPage();
        }
    }

    private onFilterMissingTargetgroupChange()
    {
        this.filterMissingTargetgroupList();
        this.showMissingTargetgroupPage();
    }

    private onMissingTargetgroupSelect(item : MetricMissingTargetgroup)
    {
        window.open(`${process.env.VUE_APP_BASE_URL}question/${item.questionid}`, '_blank')
    }
}
